import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dayjs, formatDate } from '@koob/dates';
import { FolderStatusEnum } from '@koob/enums';
import cn from '../../utils/cn';
import FolderStatusBadge from './FolderStatusBadge';
import folderStatusColor from './folderStatusColor';
import { isFolderDragEvent, getFolderDragData } from './folderDragData';

function toDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

// Earliest booking start → latest booking end, compact ("14 — 26 Aug 2026").
function getDateRange(folder) {
  const bookings = folder?.bookings ?? [];
  const starts = bookings.map((b) => toDate(b.startAt)).filter(Boolean);
  const ends = bookings.map((b) => toDate(b.endAt)).filter(Boolean);
  const start = starts.length ? new Date(Math.min(...starts)) : null;
  const end = ends.length ? new Date(Math.max(...ends)) : null;
  if (!start && !end) return null;
  if (!start || !end) return formatDate(start || end, 'DD MMM YYYY');
  const startFormat = dayjs(start).isSame(end, 'month') ? 'dd' : 'dd MMM';
  return `${formatDate(start, startFormat)} — ${formatDate(end, 'DD MMM YYYY')}`;
}

function getProvider(folder) {
  const booking = folder?.bookings?.[0];
  if (!booking) return null;
  if (booking.kind === 'dmc') {
    return (
      booking.hotel?.organization?.displayName ??
      booking.experiencesBooked?.[0]?.experience?.organizationName ??
      null
    );
  }
  return 'BedBank';
}

// A pulsing status dot (animate-ping halo + solid core). Always green here —
// unlike the status badge/icon ring elsewhere in this card, the pulsing dot
// is not meant to reflect folder status.
function StatusDot() {
  const color = '#4ade80';
  return (
    <span className="relative flex h-2 w-2 shrink-0">
      <span
        className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping"
        style={{ backgroundColor: color }}
      />
      <span
        className="relative inline-flex h-2 w-2 rounded-full"
        style={{ backgroundColor: color }}
      />
    </span>
  );
}

// One "label — value" row in the details footer.
function DetailRow({ label, children }) {
  if (!children) return null;
  return (
    <div className="flex items-center justify-between gap-2 text-[11px]">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="truncate font-medium text-foreground">{children}</span>
    </div>
  );
}

// The full active-folder detail block — reused by the expanded card and the
// collapsed-sidebar hover popover.
function ActiveFolderDetails({ folder, status, onOpen, onClose, t }) {
  const guest =
    folder?.travelers?.length > 0
      ? `${folder.travelers[0].firstName ?? ''} ${folder.travelers[0].lastName ?? ''}`.trim()
      : null;
  const guestCount = folder?.travelers?.length ?? 0;
  const agent = folder?.createdBy
    ? `${folder.createdBy.firstName ?? ''} ${folder.createdBy.lastName ?? ''}`.trim()
    : null;
  const provider = getProvider(folder);
  const dateRange = getDateRange(folder);

  return (
    <>
      {/* Header: pulsing dot + label + status badge + close */}
      <div className="flex items-center gap-2 border-0 border-b border-solid border-border px-4 py-2.5">
        <StatusDot />
        <span className="text-sm font-semibold text-foreground">
          {t('activeFolder.label')}
        </span>
        <div className="ml-auto">
          <FolderStatusBadge status={status} size="sm" />
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="border-none bg-transparent p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent cursor-pointer transition"
            aria-label={t('activeFolder.close')}
          >
            <i className="fa-light fa-xmark fa-fw text-sm" aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2 p-3">
        {/* Reference + dates */}
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-xs font-medium text-brand">
            {folder?.icr}
          </span>
          {dateRange && (
            <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
              <i className="fa-light fa-calendar" aria-hidden="true" />
              {dateRange}
            </span>
          )}
        </div>

        <p className="m-0 truncate text-sm font-semibold leading-tight text-foreground">
          {folder?.name}
        </p>

        <span className="flex min-w-0 flex-col">
          <span className="truncate text-xs font-medium text-foreground">
            {guest || t('noDetailsYet')}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <i className="fa-light fa-users" aria-hidden="true" />
            {t('kanban.nGuests', { count: guestCount })}
          </span>
        </span>

        <div className="flex flex-col gap-1.5 border-0 border-t border-solid border-border pt-2">
          <DetailRow label={t('activeFolder.agent')}>{agent}</DetailRow>
          <DetailRow label={t('activeFolder.provider')}>{provider}</DetailRow>
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex w-full items-center justify-center gap-1 border-none bg-transparent pt-1 text-[11px] font-semibold text-brand hover:underline cursor-pointer"
          >
            {t('activeFolder.open')}
            <i className="fa-light fa-arrow-right" aria-hidden="true" />
          </button>
        </div>
      </div>
    </>
  );
}

// Tracks whether a compatible drag (a client folder) is currently hovering
// this card, so it can render the dashed-orange drop-target treatment.
// Uses an enter/leave counter (via ref) rather than the boolean state alone
// so hovering over a nested child element doesn't cause dragleave flicker.
function useFolderDropTarget(onDropFolder) {
  const dragCounterRef = useRef(0);
  const [dragOver, setDragOver] = useState(false);

  if (!onDropFolder) {
    return { dragOver: false, dropHandlers: {} };
  }

  return {
    dragOver,
    dropHandlers: {
      onDragEnter: (e) => {
        if (!isFolderDragEvent(e.dataTransfer)) return;
        e.preventDefault();
        dragCounterRef.current += 1;
        setDragOver(true);
      },
      onDragOver: (e) => {
        if (!isFolderDragEvent(e.dataTransfer)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      },
      onDragLeave: (e) => {
        if (!isFolderDragEvent(e.dataTransfer)) return;
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) setDragOver(false);
      },
      onDrop: (e) => {
        if (!isFolderDragEvent(e.dataTransfer)) return;
        e.preventDefault();
        dragCounterRef.current = 0;
        setDragOver(false);
        const dropped = getFolderDragData(e.dataTransfer);
        if (dropped) onDropFolder(dropped);
      },
    },
  };
}

/**
 * Sidebar card for the active client folder.
 *
 * Presentational — the active folder + its computed status are passed in;
 * navigation/close are wired by the caller (KoobLayout) to the zustand store.
 *
 * Two states driven by `folder`:
 *  - empty (folder == null): dashed card prompting the user to browse folders.
 *  - active: status dot + badge + folder details + an Open link.
 *
 * When `collapsed` (w-16 sidebar), renders a compact status-colored folder
 * icon; hovering reveals a popover with the full active-folder info.
 *
 * Also a drop target for folders dragged from the folders list/kanban: pass
 * `onDropFolder(folder)` to accept drops (both in the empty and active
 * states) — while a compatible drag hovers, the card shows a dashed orange
 * border + light orange background.
 */
export default function ActiveFolderCard({
  folder = null,
  status = FolderStatusEnum.NEW,
  collapsed = false,
  onOpen,
  onClose,
  onBrowse,
  onDropFolder,
  className,
}) {
  const { t } = useTranslation('folders');
  const hasFolder = Boolean(folder);
  const { dragOver, dropHandlers } = useFolderDropTarget(onDropFolder);

  if (collapsed) {
    return (
      <div
        className={cn(
          'relative group flex justify-center px-2 py-2',
          className,
        )}
        {...dropHandlers}
      >
        {hasFolder ? (
          <>
            <button
              type="button"
              onClick={onOpen}
              className={cn(
                'relative border-none rounded-lg w-10 h-10 flex items-center justify-center cursor-pointer',
                dragOver
                  ? 'border-2 border-dashed border-orange-400 bg-orange-50 text-orange-600'
                  : 'bg-muted hover:bg-accent text-foreground',
              )}
              aria-label={t('activeFolder.label')}
            >
              <i className="fa-light fa-folder-user fa-fw" aria-hidden="true" />
              <span
                className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white"
                style={{ backgroundColor: folderStatusColor(status) }}
              />
            </button>
            {/* Hover popover with the full active-folder info */}
            <div className="hidden group-hover:block absolute left-full bottom-0 ml-2 z-popover w-64">
              <div className="overflow-hidden rounded-xl border border-solid border-border bg-card shadow-lg">
                <ActiveFolderDetails
                  folder={folder}
                  status={status}
                  onOpen={onOpen}
                  onClose={onClose}
                  t={t}
                />
              </div>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={onBrowse}
            className={cn(
              'border-none rounded-lg w-10 h-10 flex items-center justify-center cursor-pointer',
              dragOver
                ? 'border-2 border-dashed border-orange-400 bg-orange-50 text-orange-600'
                : 'bg-transparent border-2 border-dashed border-border text-muted-foreground hover:text-foreground',
            )}
            aria-label={t('activeFolder.emptyTitle')}
          >
            <i
              className="fa-light fa-folder-magnifying-glass fa-fw"
              aria-hidden="true"
            />
          </button>
        )}
      </div>
    );
  }

  if (!hasFolder) {
    return (
      <div className={cn('px-3 py-2', className)}>
        <div
          className={cn(
            'rounded-xl border-2 border-dashed p-4 flex flex-col items-center text-center gap-1.5',
            dragOver ? 'border-orange-400 bg-orange-50' : 'border-border',
          )}
          {...dropHandlers}
        >
          <i
            className="fa-light fa-folder-magnifying-glass text-2xl text-muted-foreground"
            aria-hidden="true"
          />
          <div className="text-sm font-semibold text-foreground">
            {t('activeFolder.emptyTitle')}
          </div>
          <div className="text-xs text-muted-foreground">
            {t('activeFolder.emptyHelp')}
          </div>
          <button
            type="button"
            onClick={onBrowse}
            className="mt-1 inline-flex items-center gap-2 border-none rounded-lg px-3 py-1.5 text-sm font-medium text-foreground bg-muted hover:bg-accent cursor-pointer"
          >
            <i className="fa-light fa-folder-open fa-fw" aria-hidden="true" />
            {t('activeFolder.browse')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'm-3 overflow-hidden rounded-xl',
        dragOver
          ? 'border-2 border-dashed border-orange-400 bg-orange-50'
          : 'border border-solid border-border bg-card',
        className,
      )}
      {...dropHandlers}
    >
      <ActiveFolderDetails
        folder={folder}
        status={status}
        onOpen={onOpen}
        onClose={onClose}
        t={t}
      />
    </div>
  );
}
